package gr.tech.pdfgenerator.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleInfo
import com.varabyte.kobweb.silk.components.icons.fa.FaClone
import com.varabyte.kobweb.silk.components.icons.fa.FaEllipsisVertical
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorPalettes
import gr.tech.pdfgenerator.components.layouts.AuthPageLayout
import gr.tech.pdfgenerator.components.widgets.ConfirmModal
import gr.tech.pdfgenerator.infrastructure.api.duplicateProjectByID
import gr.tech.pdfgenerator.infrastructure.api.fetchProjects
import gr.tech.pdfgenerator.infrastructure.api.projectSaveToDatabase
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.models.projects.ProjectsDTO
import gr.tech.pdfgenerator.models.user.USER_ROLES
import gr.tech.pdfgenerator.styles.GeneralButtonStyle
import gr.tech.pdfgenerator.styles.LargeColumnStyle
import gr.tech.pdfgenerator.styles.MediumColumnStyle
import gr.tech.pdfgenerator.styles.TableHeaderStyle
import gr.tech.pdfgenerator.styles.TableRowButtonStyle
import gr.tech.pdfgenerator.styles.TableRowHoverStyle
import gr.tech.pdfgenerator.styles.Theme
import gr.tech.pdfgenerator.styles.overlayTextStyle
import gr.tech.pdfgenerator.util.CONSTANTS
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY2
import gr.tech.pdfgenerator.util.formatDate
import gr.tech.pdfgenerator.util.truncate
import gr.web.icyleds.components.sections.NavHeader
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page(routeOverride = "projects")
@Composable
fun ProjectsPage() {
    AuthPageLayout(
        title = "Project Selection",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        ProjectsScreen()
    }
}

data class projectsPageState(
    var showLoading: Boolean = true,

    var showDuplicateConfirm: Boolean = false,
    var toDuplicateId: Int? = null,

    val projectType: String = "",
    var errorText: String = " ",
)


@Composable
fun ProjectsScreen() {
    val ctx = rememberPageContext()
    val breakpoint = rememberBreakpoint()
    val scope = rememberCoroutineScope()

    var projectsRowsToSkip by remember { mutableStateOf(0) }
    var projectsShowMoreVisibility by remember { mutableStateOf(false) }
    val projectsList = remember { mutableStateListOf<ProjectsDTO>() }

    var uiState by remember { mutableStateOf(projectsPageState()) }

    LaunchedEffect(Unit) {
        GeneratorData.unloadProject()

        fetchProjects(
            skip = projectsRowsToSkip,
            onSuccess = {
                projectsList.clear()
                projectsList.addAll(it)
                projectsRowsToSkip += CONSTANTS.ROWS_PER_FETCH_REQ
                projectsShowMoreVisibility = it.size >= CONSTANTS.ROWS_PER_FETCH_REQ
                uiState = uiState.copy(showLoading = false)
            },
            onError = {
                console.log(it)
            }
        )
    }

    NavHeader()

    if (uiState.showDuplicateConfirm) {
        ConfirmModal(
            actionText = "Duplicate Project",
            subText = "This will create a new duplicated copy of this project.",
            onConfirm = {
                uiState = uiState.copy(
                    showDuplicateConfirm = false
                )
                scope.launch {
                    uiState.toDuplicateId?.let {
                        duplicateProjectByID(
                            id = it,
                            onSuccess = {
                                window.location.reload()
                            },
                            onError = {
                                console.log("Error duplicating project: $it")
                            }
                        )
                    }
                }
            },
            onDismiss = {
                uiState = uiState.copy(
                    showDuplicateConfirm = false
                )
            }
        )
    }

    Box(
        modifier = Modifier
            .fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .margin(top = 24.px)
                .width(1200.px)
                .height(600.px)
                .padding(12.px)
                .backgroundColor(Colors.LightGray)
                .borderRadius(5.px)
        ) {

            // Title and New Button Row
            Row(
                modifier = Modifier
                    .padding(bottom = 8.px)
                    .margin(bottom = 8.px)
                    .borderBottom(1.px, LineStyle.Solid, Colors.DarkGray)
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                H1(
                    attrs = Modifier
                        .fontFamily(FONT_FAMILY2)
                        .fontSize(1.5.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(Colors.Black)
                        .toAttrs()
                ) {
                    Text("Select Project")
                }
                Spacer()
                Button(
                    colorPalette = ColorPalettes.Green,
                    size = ButtonSize.MD,
                    modifier = Modifier
                        .color(Colors.White),
                    onClick = {
                        ctx.router.tryRoutingTo("/inputs")
                    }
                )  {
                    Text("Create New Project")
                }
            }

            // Table for Selecting Projects
            Column(
                modifier = Modifier
                    .overflow(Overflow.Auto)
                    .fillMaxSize()
                    .borderRadius(5.px)
                    .border(1.px, LineStyle.Solid, Colors.DarkGray),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Table(
                    attrs = Modifier
                        .margin(0.px)
                        .fillMaxWidth()
                        .classNames("table")
                        .toAttrs()
                ) {

                    // Table Head and Headers
                    Thead(
                        attrs = Modifier
                            .toAttrs {
                                style {
                                    property("table-layout", "fixed")
                                }
                            }
                    ) {
                        Tr(
                            attrs = Modifier
                                .fillMaxWidth()
                                .toAttrs()
                        ) {
                            Th(
                                TableHeaderStyle.toModifier()
                                    .then(MediumColumnStyle).toAttrs()) {
                                Text("Title")
                            }
                            Th(
                                TableHeaderStyle.toModifier()
                                    .then(MediumColumnStyle).toAttrs()) {
                                Text("Updated")
                            }
                            Th(
                                TableHeaderStyle.toModifier()
                                    .then(MediumColumnStyle).toAttrs()) {
                                Text("Created")
                            }
                            Th(
                                TableHeaderStyle.toModifier()
                                    .then(LargeColumnStyle).toAttrs()) {
                                Text("Description")
                            }
                        }
                    }

                    // Table Body with Project Selecting rows
                    Tbody {
                        projectsList.forEach { project ->
                            Tr(
                                attrs = TableRowHoverStyle.toModifier()
                                    .onClick {
                                        ctx.router.tryRoutingTo("/inputs?projectid=${project.id}")
                                    }
                                    .toAttrs()
                            ) {
                                Td(MediumColumnStyle.toAttrs()) {
                                    Text(project.projectTitle.toString())
                                }
                                Td(MediumColumnStyle.toAttrs()) {
                                    Column {
                                        SpanText(text = formatDate(project.updatedAt.toString()))
                                        SpanText(text = "${project.updatedBy}")
                                    }
                                }
                                Td(MediumColumnStyle.toAttrs()) {
                                    Column {
                                        SpanText(text = formatDate(project.createdAt.toString()))
                                        SpanText(text = "${project.createdBy}")
                                    }
                                }
                                Td(LargeColumnStyle.toAttrs()) {
                                    Text(project.description?.truncate(50) ?: "-")
                                }
                                Div(
                                    attrs = TableRowButtonStyle.toAttrs()
                                ) {
                                    Button(
                                        size = ButtonSize.MD,
                                        modifier = GeneralButtonStyle.toModifier()
                                            .fillMaxWidth()
                                            .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                                            .color(Colors.White)
                                            .attrsModifier {
                                                title("Duplicate Project")
                                            },
                                        onClick = {
                                            uiState = uiState.copy(
                                                showDuplicateConfirm = true,
                                                toDuplicateId = project.id
                                            )
                                        }
                                    ) {
                                        FaClone(size = IconSize.SM, modifier = Modifier.color(Theme.MediumGreen.rgb))
                                    }
                                }
                            }
                        }
                    }
                }

                // Show More Button
                Row(
                    modifier = Modifier
                        .margin(topBottom = 12.px)
                        .height(34.px),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Button(
                        colorPalette = ColorPalettes.Green,
                        size = ButtonSize.MD,
                        modifier = Modifier
                            .display(if (projectsShowMoreVisibility) DisplayStyle.Block else DisplayStyle.None)
                            .color(Colors.White),
                        onClick = {
                            scope.launch {
                                fetchProjects(
                                    skip = projectsRowsToSkip,
                                    onSuccess = {
                                        if (it.isNotEmpty()) {
                                            projectsList.addAll(it)
                                            projectsRowsToSkip += CONSTANTS.ROWS_PER_FETCH_REQ
                                            projectsShowMoreVisibility = it.size >= CONSTANTS.ROWS_PER_FETCH_REQ
                                            if (it.size < CONSTANTS.ROWS_PER_FETCH_REQ) {
                                                projectsShowMoreVisibility = false
                                            }
                                        }  else {
                                            projectsShowMoreVisibility = false
                                        }
                                    },
                                    onError = {
                                        println(it)
                                    }
                                )
                            }
                        }
                    )  {
                        Text("Show More")
                    }
                }
            }
        }
    }
}